
import { useState } from 'react';

interface ProfilePhotoUploadProps {
  currentPhoto?: string;
  onPhotoChange: (photo: string) => void;
}

export default function ProfilePhotoUpload({ currentPhoto, onPhotoChange }: ProfilePhotoUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploading(true);
      
      // Simulation d'upload
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setTimeout(() => {
          onPhotoChange(result);
          setIsUploading(false);
          setShowPhotoModal(false);
        }, 1500);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    onPhotoChange('');
    setShowPhotoModal(false);
  };

  return (
    <>
      <div className="relative">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg">
          {currentPhoto ? (
            <img 
              src={currentPhoto} 
              alt="Photo de profil" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-teal-600 flex items-center justify-center">
              <i className="ri-user-line text-white text-2xl"></i>
            </div>
          )}
        </div>
        
        <button
          onClick={() => setShowPhotoModal(true)}
          className="absolute -bottom-2 -right-2 w-8 h-8 bg-teal-600 hover:bg-teal-700 text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
        >
          <i className="ri-camera-line text-sm"></i>
        </button>
      </div>

      {/* Modal de gestion de photo */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Photo de profil</h3>
              <button
                onClick={() => setShowPhotoModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="text-center mb-6">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 mx-auto mb-4">
                {currentPhoto ? (
                  <img 
                    src={currentPhoto} 
                    alt="Photo de profil" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-teal-600 flex items-center justify-center">
                    <i className="ri-user-line text-white text-3xl"></i>
                  </div>
                )}
              </div>
              
              {isUploading && (
                <div className="mb-4">
                  <div className="flex items-center justify-center space-x-2 text-teal-600">
                    <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm">Téléchargement en cours...</span>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
                <div className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 px-4 rounded-lg font-medium text-center cursor-pointer transition-colors">
                  <i className="ri-upload-line mr-2"></i>
                  Choisir une photo
                </div>
              </label>

              {currentPhoto && (
                <button
                  onClick={removePhoto}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                  disabled={isUploading}
                >
                  <i className="ri-delete-bin-line mr-2"></i>
                  Supprimer la photo
                </button>
              )}

              <button
                onClick={() => setShowPhotoModal(false)}
                className="w-full border border-gray-300 hover:border-gray-400 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
            </div>

            <div className="mt-4 text-xs text-gray-500 text-center">
              Formats acceptés : JPG, PNG, GIF (max 5 MB)
            </div>
          </div>
        </div>
      )}
    </>
  );
}
